/**
 * TrackingInit — fires once on app mount.
 *
 * 1. Re-applies cookie consent to Google Consent Mode v2 — the visitor's choice,
 *    else the admin default cached from the last visit (required on every
 *    session — CM v2 is not persistent across page loads)
 * 2. Captures UTM / referrer / affiliate on first visit (first-touch attribution)
 *    and the latest ad click IDs for 90 days (last-click, for conversion uploads)
 * 3. Pushes te_user_init with visitor ID + funnel stage + authenticated user ID
 *    so GTM can set user_id on GA4 and sync remarketing segments
 */

import { useEffect } from "react";
import { initUserContext } from "@/lib/analytics";
import { captureLeadSource } from "@/hooks/useLeadSource";
import { captureAdClick } from "@/lib/attribution";
import { initLiveChatTracking } from "@/lib/livechat";
import { getEffectiveConsent, updateGoogleConsent } from "@/lib/consent";
import { supabase } from "@/integrations/supabase/client";

const TrackingInit = () => {
  useEffect(() => {
    // 1. Re-apply consent to CM v2 (must run before gtag calls)
    const consent = getEffectiveConsent();
    if (consent) updateGoogleConsent(consent.analytics, consent.marketing);

    // 2. Capture lead source on first visit + latest ad click; report live chats with it
    captureLeadSource();
    captureAdClick();
    initLiveChatTracking();

    // 3. Push user context — include authenticated user ID if available
    const t = setTimeout(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        initUserContext(session?.user?.id);
      } catch {
        // navigator.locks may throw AbortError when stolen by another tab — safe to ignore
        initUserContext(undefined);
      }
    }, 200);

    return () => clearTimeout(t);
  }, []);

  return null;
};

export default TrackingInit;
